import { FC, useMemo, useRef } from 'react';
import { FCX } from '@datassential/platform-ui-lib';
import Table from '@components/new/Table/Table';
import { iTableConfigItem } from '@components/new/Table/Table.helpers';
import { SalesFocusChain } from '@apiHooks/useSalesFocusData';
import NewTooltip from '@components/new/NewTooltip/NewTooltip';
import useIsTruncated from '@hooks/useIsTruncated';
import HomeSectionFooter from '@pages/Home/components/HomeSectionFooter';
import AccessLink from "@components/AccessLink/AccessLink";
import { buildChainProfileUrl, formatSalesGrowth, formatUnitGrowth, } from './HomeSalesFocusChains.helpers';
import { EMPTY_CHAINS_MESSAGE, MORE_CHAIN_TARGETS_LABEL, } from './HomeSalesFocusChains.constants';

interface SalesFocusChainsTableProps {
    rows: SalesFocusChain[];
    moreTargetsUrl: string;
}

const TruncatedLinkCell: FC<{ text: string; href: string }> = ({ text, href }) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const isTruncated = useIsTruncated(ref, text);
    return (
        <div className="HomeSalesFocusChains__truncated-cell">
            <a
                ref={ref}
                href={href}
                target="_blank"
                rel="noreferrer"
                className="HomeSalesFocusChains__chain-link HomeSalesFocusChains__truncated-text"
            >
                {text}
            </a>
            {isTruncated && <NewTooltip position={['center', 'above']}>{text}</NewTooltip>}
        </div>
    );
};

const ColHeader = ({ label, sub }: { label: string; sub: string }) => (
    <span>
        {label}
        <br/>
        <span className="HomeSalesFocusChains__col-sub">{sub}</span>
    </span>
);

const SalesFocusChainsTable: FCX<SalesFocusChainsTableProps> = ({ rows, moreTargetsUrl }) => {
    const config: iTableConfigItem[] = useMemo(
        () => [
            {
                title: '',
                Value: ({ index }) => (
                    <TruncatedLinkCell
                        text={rows[index].chainName ?? ''}
                        href={buildChainProfileUrl(rows[index].chainId ?? 0)}
                    />
                ),
            },
            {
                title: 'HQ Metro',
                textCentered: true,
                Value: ({ index }) => <span>{rows[index].hqMetro ?? ''}</span>,
            },
            {
                title: 'HQ State',
                textCentered: true,
                Value: ({ index }) => <span>{rows[index].hqState ?? ''}</span>,
            },
            {
                renderTitle: <ColHeader label="Units" sub="2025"/>,
                textCentered: true,
                Value: ({ index }) => (
                    <span>{(rows[index].units ?? 0).toLocaleString()}</span>
                ),
            },
            {
                renderTitle: <ColHeader label="Unit Growth" sub="1 yr"/>,
                textCentered: true,
                Value: ({ index }) => (
                    <span>{formatUnitGrowth(rows[index].unitGrowth ?? 0)}</span>
                ),
            },
            {
                renderTitle: <ColHeader label="Sales Growth" sub="1 yr"/>,
                textCentered: true,
                Value: ({ index }) => (
                    <span>{formatSalesGrowth(rows[index].salesGrowth ?? 0)}</span>
                ),
            },
        ],
        [rows],
    );

    if (rows.length === 0) {
        return (
            <div className="SalesFocusChainsTable">
                <div className="HomeSalesFocusChains__empty-state">
                    {EMPTY_CHAINS_MESSAGE}
                </div>
                <HomeSectionFooter>
                    <AccessLink
                        url={moreTargetsUrl}
                        text={MORE_CHAIN_TARGETS_LABEL}
                        onContactUsClick={() => undefined}
                        testId="linkSalesFocusMoreChainTargets"
                    />
                </HomeSectionFooter>
            </div>
        );
    }

    return (
        <div className="SalesFocusChainsTable">
            <Table
                config={config}
                rowsCount={rows.length}
                hasInnerScroll={false}
                isCopyButtonHidden
            />
            <HomeSectionFooter>
                <AccessLink
                    url={moreTargetsUrl}
                    text={MORE_CHAIN_TARGETS_LABEL}
                    onContactUsClick={() => undefined}
                    testId="linkSalesFocusMoreChainTargets"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default SalesFocusChainsTable;
